import { action, computed, observable, runInAction, makeObservable } from "mobx";
import { IAutocomplete } from "../modelo/Autocomplete";
import { ProcessoExterno } from "../modelo/ProcessoExterno";
import UtilNumeroProcesso from "../utils/UtilNumeroProcesso";
import BaseFiltroStore from "./BaseFiltroStore";
import ImportacaoProcessosStore from "./ImportacaoProcessosStore";
import MensagemStore from "./MensagemStore";
import ParametroStore from "./ParametroStore";
import PaginacaoImportacaoStore from "./PaginacaoImportacaoStore";

class FiltroImportacaoStore extends BaseFiltroStore {
    numerosFiltro: string = '';
    numerosFiltroAplicado: string = '';

    constructor() {
        super();

        makeObservable(this, {
            numerosFiltro: observable,
            numerosFiltroAplicado: observable,
            limparFiltros: action,
            limparFiltroNumero: action,
            classesUnicasFormatoAutoComplete: computed,
            tiposPeticaoPendenteAutocomplete: computed,
            ojAutoComplete: computed
        });
    }

    nenhumParametroSelecionado() {
        return this.nenhumFiltroImportacaoSemNumero() && !this.numerosFiltro?.trim();
    }

    nenhumParametroAplicado() {
        return this.nenhumFiltroImportacaoSemNumero() && !this.numerosFiltroAplicado?.trim();
    }

    nenhumFiltroImportacaoSemNumero() {
        const todasAsOrigensSelecionadas = this.origem?.includes('P') && this.origem?.includes('L');
        const possuiFiltroDeOrigem = !!this.origem && !todasAsOrigensSelecionadas;

        return (this.orgaoJudicanteSelecionadoFilter?.length ?? 0) === 0
            && !possuiFiltroDeOrigem
            && (this.classeProcessoFilter?.length ?? 0) === 0;
    }

    limparFiltros = () => {
        this.numerosFiltro = '';
        this.numerosFiltroAplicado = '';
        this.limparFiltrosBase();
    }

    limparFiltroNumero = () => {
        this.numerosFiltro = '';
        this.numerosFiltroAplicado = '';

        if (this.nenhumFiltroImportacaoSemNumero()) {
            this.filtroAplicado = false;
            PaginacaoImportacaoStore.paginaFiltro = 1;
            ImportacaoProcessosStore.processosQuePodemSerImportados.forEach(processo =>
                processo.resetMostrar && processo.resetMostrar()
            );
            return;
        }

        this.procurar(false);
    };

    get classesUnicasFormatoAutoComplete() {
        const classes: IAutocomplete<string>[] = [];

        ImportacaoProcessosStore.processosQuePodemSerImportados.forEach(proc => {
            classes.push({
                texto: proc.classe,
                // textoSecundario: proc.classeJudicial.descricao,
                parametrosBusca: [proc.classe],
                valor: proc.classe
            });
        })
        return this.retornaValoresUnicosDeUmArray(classes, 'texto');
    }

    get tiposPeticaoPendenteAutocomplete(): IAutocomplete<string>[] {
        const tipos = ParametroStore.tiposPeticaoPendente.map(t => {

            const autocomplete: IAutocomplete<string> = {
                parametrosBusca: [t.nomeTipoProcessoDocumento],
                texto: t.nomeTipoProcessoDocumento,
                valor: t.nomeTipoProcessoDocumento
            }

            return autocomplete
        })
        return [{
            parametrosBusca: ['Qualquer tipo'],
            texto: 'Qualquer tipo',
            valor: 'Qualquer tipo'
        }].concat(tipos);
    }

    aplicarFiltros = (listaDeProcessos: ProcessoExterno[]) => {
        if (this.nenhumParametroAplicado() || !this.filtroAplicado) {
            return listaDeProcessos;
        }
        return listaDeProcessos.filter(proc => {
            return (
                this.filtrarPorListaDeNumeros(proc)
                && this.filtrarPorOJ(proc)
                && this.filtrarPorOrigem(proc)
                && this.filtrarPorClasse(proc)
            );
        });
    }

    procurar = (mostrarMensagem: boolean = true) => {
        // Quando existirem filtros, aplicá-los a todos os processos (não apenas à página atual)
        if (!this.nenhumParametroSelecionado()) {
            runInAction(() => {
                PaginacaoImportacaoStore.paginaFiltro = 1;
                this.numerosFiltroAplicado = this.numerosFiltro;
                this.filtroAplicado = true;
            });

            runInAction(() => {
                const processos = ImportacaoProcessosStore.processosQuePodemSerImportados || [];
                const length = processos.length;

                // Se numerosFiltro for uma lista em múltiplas linhas, prepará-la (espelha a lógica de filtrarPorListaDeNumeros)
                // Aplicar os filtros por processo e definir o valor de mostrar de acordo
                for (let i = 0; i < length; i++) {
                    const proc = processos[i];
                    // Desmarcar e redefinir o estado anterior de mostrar
                    proc.selecionar && proc.selecionar(false);
                    proc.resetMostrar && proc.resetMostrar();

                    const achou = this.filtrarPorListaDeNumeros(proc)
                        && this.filtrarPorOJ(proc)
                        && this.filtrarPorOrigem(proc)
                        && this.filtrarPorClasse(proc);

                    // Garantir que seja booleano
                    proc.setMostrar && proc.setMostrar(!!achou);
                }
            });

        } else {
            runInAction(() => {
                this.filtroAplicado = false;
                this.numerosFiltroAplicado = '';
                // reset mostrar for all processos so full list is shown
                ImportacaoProcessosStore.processosQuePodemSerImportados.forEach(proc => proc.resetMostrar && proc.resetMostrar());
            });
            mostrarMensagem && MensagemStore.apresentarInformacao('Não há parâmetros de pesquisa.');
        }
    }

    filtrarPorListaDeNumeros = (proc: ProcessoExterno) => {
        if (!this.numerosFiltroAplicado) {
            return proc;
        }

        const numerosNoFiltro = this.numerosFiltroAplicado
            .split(/;|\n/)
            .map(num => num?.trim())
            .filter(num => num?.length > 0);

        if (numerosNoFiltro.length === 0) {
            return proc;
        }

        const achou = numerosNoFiltro.find(numeroProcessoFilter => {
            const numeroProcesso = UtilNumeroProcesso.matchObjetoNumeroProcesso(proc.numeroProcesso);
            const filtro = UtilNumeroProcesso.matchObjetoNumeroProcesso(numeroProcessoFilter);
            if (!filtro?.validoFormato) {
                return false;
            }

            if (filtro?.numero && !UtilNumeroProcesso.numeroEhCompativelComPrefixoZero(numeroProcesso.numero, filtro.numero)) {
                return false;
            }
            if (!UtilNumeroProcesso.campoEhCompativelPorPrefixo(numeroProcesso.digito, filtro?.digito)) {
                return false;
            }
            if (!UtilNumeroProcesso.campoEhCompativelPorPrefixo(numeroProcesso.ano, filtro?.ano)) {
                return false;
            }
            if (!UtilNumeroProcesso.campoEhCompativelPorPrefixo(numeroProcesso.justica, filtro?.justica)) {
                return false;
            }
            if (!UtilNumeroProcesso.campoEhCompativelPorPrefixo(numeroProcesso.tribunal, filtro?.tribunal)) {
                return false;
            }
            if (!UtilNumeroProcesso.campoEhCompativelPorPrefixo(numeroProcesso.vara, filtro?.vara)) {
                return false;
            }

            return true;
        });
        return achou;
    }

    get ojAutoComplete() {
        return ImportacaoProcessosStore.processosQuePodemSerImportados?.reduce(
            (acc, processo) => {
                const siglaOjcProcesso = processo.orgaoJulgadorColegiado?.sigla?.trim();
                if (siglaOjcProcesso && !acc.some(ojc => ojc.valor === siglaOjcProcesso)) {
                    acc.push({
                        texto: siglaOjcProcesso,
                        valor: siglaOjcProcesso,
                        parametrosBusca: [siglaOjcProcesso]
                    });
                }
                return acc;
            }, []) || [];
    }

    filtrarPorOJ = (proc: ProcessoExterno) => {
        if (this.orgaoJudicanteSelecionadoFilter?.length === 0) {
            return proc;
        }
        return this.orgaoJudicanteSelecionadoFilter.find(oj => oj === proc.orgaoJulgadorColegiado.sigla);
    }

    filtrarPorOrigem = (proc: ProcessoExterno) => {
        if (!this.origem) {
            return proc;
        }
        return (this.origem.includes('L') && proc.ehLegado) || (this.origem.includes('P') && proc.ehPje);
    }

    filtrarPorClasse = (proc: ProcessoExterno) => {
        if (this.classeProcessoFilter.length === 0) {
            return proc;
        }
        return this.classeProcessoFilter.find(classe => {
            classe = classe?.replace(/\s/g, '');
            return proc.classe === classe
        });
    }
}

export default new FiltroImportacaoStore();
